// Paid modules, browser side: ask for offers (one price per person, decided
// by supabase/functions/_shared/monetization.ts), pay, verify identity, and
// read who is featured or verified. Nothing renders when a module is off.
// Plan: HANDOFF.md §9.14.
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useIsStaff } from "@/hooks/useIsStaff";
import { useTenant } from "@/hooks/useTenant";
import { getTrackingContext } from "@/lib/track";
import { cachedVisitorContext, loadVisitorContext } from "@/lib/visitorContext";
import { formatUsd } from "../../supabase/functions/_shared/monetization";

export { formatUsd };

export type ModuleKey = "listing_fee" | "featured_listing" | "bump_72h" | "verified_landlord" | "verified_renter" | "priority_pass";
export type BuyerType = "renter" | "owner";

/** What each kind of user can buy, in the order /pricing shows it. */
export const MODULES_FOR: Record<BuyerType, ModuleKey[]> = {
  renter: ["verified_renter", "priority_pass"],
  owner: ["listing_fee", "featured_listing", "bump_72h", "verified_landlord"],
};

export interface Offer {
  state: "available" | "off" | "not_for_you" | "active" | "full" | "no_price" | "not_your_listing";
  module: ModuleKey;
  name?: string;
  description?: string;
  kind?: "one_time" | "duration" | "subscription";
  amountCents?: number;
  durationHours?: number | null;
  interval?: string | null;
  quote?: string;
  /** Stripe is on its test key (only the team sees offers then): pay with a test card. */
  testMode?: boolean;
  /** When state is "active": when it runs out (null for open-ended). */
  endsAt?: string | null;
}

/** "US$29 for 30 days", "US$199 a year", "US$9 for 72 hours", "Free". */
export function priceLabel(o: Offer): string {
  if (o.amountCents == null) return "";
  const p = formatUsd(o.amountCents);
  if (o.kind === "subscription") return `${p} a ${o.interval === "month" ? "month" : "year"}`;
  if (o.kind === "duration" && o.durationHours) {
    return o.durationHours % 24 === 0 && o.durationHours >= 96
      ? `${p} for ${o.durationHours / 24} days`
      : `${p} for ${o.durationHours} hours`;
  }
  return p;
}

// A price shown to someone we could not remember (no analytics consent, not
// logged in). Sent at signup so that person stays on the same (control) price.
const SAW_PRICE_KEY = "msth_saw_price_v1";
const markSawPrice = () => { try { localStorage.setItem(SAW_PRICE_KEY, "1"); } catch { /* private mode */ } };
const sawPrice = () => { try { return localStorage.getItem(SAW_PRICE_KEY) === "1"; } catch { return false; } };

export async function fetchOffers(args: {
  tenant: string;
  modules: ModuleKey[];
  subject?: string | null;
  declaredType?: BuyerType | null;
  surface?: string;
  browse?: boolean;
  loggedIn: boolean;
}): Promise<{ offers: Offer[]; payments: boolean; testMode: boolean }> {
  const edge = cachedVisitorContext() ?? (await loadVisitorContext());
  const visitorId = getTrackingContext().visitorId;
  const { data, error } = await supabase.functions.invoke("monetization-quote", {
    body: {
      tenant: args.tenant, modules: args.modules, subject: args.subject ?? null, declaredType: args.declaredType ?? null,
      surface: args.surface ?? null, browse: !!args.browse, visitorId, edge, sawLoggedOutPrice: sawPrice(),
    },
  });
  if (error || !data) return { offers: [], payments: false, testMode: false };
  const offers = ((data.offers ?? []) as Offer[]).map((o) => (data.testMode ? { ...o, testMode: true } : o));
  if (!args.loggedIn && !visitorId && offers.some((o) => o.state === "available")) markSawPrice();
  return { offers, payments: !!data.payments, testMode: !!data.testMode };
}

/**
 * Offers for this person, refreshed when they log in or out. Errors mean
 * "nothing to offer". `tenant` is the market the offer is priced for; it
 * defaults to the site's, and a listing passes its own (Oct 19: an owner's
 * Tamarindo home showed nothing on the Santa Teresa dashboard because it
 * was priced against the wrong market).
 */
export function useOffers(modules: ModuleKey[], opts: { subject?: string | null; declaredType?: BuyerType | null; surface?: string; browse?: boolean; enabled?: boolean; tenant?: string | null } = {}) {
  const site = useTenant();
  const tenant = opts.tenant || site.id;
  const { user } = useAuth();
  return useQuery({
    queryKey: ["offers", tenant, user?.id ?? null, modules.join(","), opts.subject ?? null, opts.declaredType ?? null, !!opts.browse],
    enabled: opts.enabled !== false && modules.length > 0,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: () => fetchOffers({ tenant, modules, subject: opts.subject, declaredType: opts.declaredType, surface: opts.surface, browse: opts.browse, loggedIn: !!user }),
  });
}

async function invokeCheckout(body: Record<string, unknown>): Promise<{ url?: string; next?: string; status?: string }> {
  const { data, error } = await supabase.functions.invoke("create-checkout", { body });
  if (error) {
    let msg = "Payment could not start. Try again in a moment.";
    try {
      const b = await (error as { context?: Response }).context?.json();
      if (b?.error) msg = b.error;
    } catch { /* keep the default */ }
    throw new Error(msg);
  }
  return data ?? {};
}

/** Pay for an offer: always Stripe Checkout (an identity check starts only after paying). */
export async function buyOffer(o: Offer, opts: { returnPath?: string; cancelPath?: string } = {}): Promise<{ url?: string; next?: string }> {
  if (!o.quote) throw new Error("This price has expired. Refresh the page to see it again.");
  // The same flag as the quote, so checkout prices this person the same way.
  return invokeCheckout({ action: "checkout", quote: o.quote, visitorId: getTrackingContext().visitorId, sawLoggedOutPrice: sawPrice(), returnPath: opts.returnPath, cancelPath: opts.cancelPath });
}

/** Switch a subscription's renewal off (renew = false) or back on, from our own account page. */
export async function setRenewal(subscriptionId: string, renew: boolean): Promise<boolean> {
  const { data, error } = await supabase.functions.invoke("create-checkout", { body: { action: "renewal", subscription_id: subscriptionId, renew } });
  if (error) throw new Error((data as { error?: string } | null)?.error ?? error.message);
  return !!(data as { cancel_at_period_end?: boolean }).cancel_at_period_end;
}

/** The Stripe page that collects a new card for a subscription (setup mode, no charge). */
export async function changeCardUrl(subscriptionId: string, returnPath = "/profile"): Promise<string> {
  const { data, error } = await supabase.functions.invoke("create-checkout", { body: { action: "card", subscription_id: subscriptionId, returnPath } });
  if (error || !(data as { url?: string })?.url) throw new Error((data as { error?: string } | null)?.error ?? error?.message ?? "Could not open the card page.");
  return (data as { url: string }).url;
}

export const startIdentityCheck = (kind: "renter" | "landlord", tenant: string) =>
  invokeCheckout({ action: "verify", kind, tenant, returnPath: `/verify?kind=${kind}` });

// ── What everyone can see ─────────────────────────────────────────────────
export interface Promotions {
  featured: Set<string>;
  /** Property id → when its bump started (the newest bump sorts first). */
  bumped: Map<string, number>;
  verifiedOwners: Set<string>;
  verifiedRenters: Set<string>;
}
const EMPTY: Promotions = { featured: new Set(), bumped: new Map(), verifiedOwners: new Set(), verifiedRenters: new Set() };

/** Featured and bumped listings in this market, and who is identity verified. Empty until the tables exist. */
/** What the promotions query stores: plain JSON, because prerendered pages
 *  embed the query cache as JSON (src/lib/queryClient.ts) and a Set or Map
 *  would come back as {} and crash every card (2026-10-12 outage). */
export interface PromotionsData {
  featured: string[];
  bumped: [string, number][];
  verifiedOwners: string[];
  verifiedRenters: string[];
}
const list = <T,>(x: unknown): T[] => (Array.isArray(x) ? (x as T[]) : []);

/** Sets and a Map from the stored data; anything malformed reads as empty. */
export function toPromotions(d: Partial<PromotionsData> | null | undefined): Promotions {
  if (!d) return EMPTY;
  return {
    featured: new Set(list<string>(d.featured)),
    bumped: new Map(list<[string, number]>(d.bumped).filter((e) => Array.isArray(e) && e.length === 2)),
    verifiedOwners: new Set(list<string>(d.verifiedOwners)),
    verifiedRenters: new Set(list<string>(d.verifiedRenters)),
  };
}

/** Featured and bumped listings in this market, and who is identity verified. Empty until the tables exist. */
export function usePromotions(): Promotions {
  const tenant = useTenant();
  // Renters see live promotions only. The team also sees test ones, so a
  // purchase on Stripe's test key can be checked on the real pages (Oct 20).
  const { data: staff = false } = useIsStaff();
  const { data } = useQuery({
    queryKey: ["promotions", tenant.id, staff],
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async (): Promise<PromotionsData> => {
      const [promos, badges] = await Promise.all([
        supabase.from("property_promotions" as never).select("property_id, module_key, starts_at, livemode").eq("tenant_id", tenant.id),
        supabase.from("verified_badges" as never).select("user_id, kind").limit(5000),
      ]);
      const out: PromotionsData = { featured: [], bumped: [], verifiedOwners: [], verifiedRenters: [] };
      for (const p of ((promos.data ?? []) as { property_id: string; module_key: string; starts_at: string; livemode?: boolean }[])) {
        if (p.livemode === false && !staff) continue;
        if (p.module_key === "featured_listing") out.featured.push(p.property_id);
        else out.bumped.push([p.property_id, Date.parse(p.starts_at)]);
      }
      for (const b of ((badges.data ?? []) as { user_id: string; kind: string }[])) {
        (b.kind === "landlord" ? out.verifiedOwners : out.verifiedRenters).push(b.user_id);
      }
      return out;
    },
  });
  return useMemo(() => toPromotions(data as Partial<PromotionsData> | undefined), [data]);
}

/** Requests from identity-verified renters first; otherwise the order is kept. */
export function verifiedFirst<T extends { renter_id: string }>(list: readonly T[], verified: ReadonlySet<string>): T[] {
  return [...list.filter((r) => verified.has(r.renter_id)), ...list.filter((r) => !verified.has(r.renter_id))];
}

/**
 * One line for the team about the state of payments, from the quote's flags.
 * Test mode hides every offer from people who are not admins or developers
 * (so a real owner never reaches a test checkout), which is why a test owner
 * account sees "no paid extras" until it gets the developer role (Oct 20).
 */
export function describePayments(q: { payments: boolean; testMode: boolean } | undefined): string {
  if (!q) return "Payments: checking.";
  if (q.testMode) return "Payments: Stripe test mode. Only admins and developers see offers and pay with the test card. To test as an owner, give that account the developer role.";
  if (q.payments) return "Payments: live. Owners and renters see offers and pay with real cards.";
  return "Payments: not set up. The Stripe key or the quote signing secret is missing in Supabase, so nobody sees offers.";
}

